import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import ColegioService from '../services/ColegioService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import AdminMenu from './common/menus/AdminMenu';
import { useAuth } from '../contexts/AuthContext';
import cursoService from '../services/CursoService';
import materiaService from '../services/MateriaService';
import divisionService from '../services/DivisionService';
import usuarioService from '../services/UsuarioService';
import { ADMIN_DASHBOARD_MENU_ITEMS, navigateAdminMenuItem } from '../utils/adminMenu';

const summarizeAssociation = (res) => {
  const nuevos = res?.asociadosNuevos || res?.AsociadosNuevos || [];
  const reactivados = res?.reactivados || res?.Reactivados || [];
  const yaAsociados = res?.yaAsociados || res?.YaAsociados || [];
  const noEncontrados = res?.profesoresNoEncontrados || res?.ProfesoresNoEncontrados || [];
  const noRol = res?.profesoresNoRol || res?.ProfesoresNoRol || [];
  const noColegio = res?.profesoresNoColegio || res?.ProfesoresNoColegio || [];
  const parts = [];
  if (nuevos.length) parts.push(`Asociados nuevos: ${nuevos.length}`);
  if (reactivados.length) parts.push(`Reactivados: ${reactivados.length}`);
  if (yaAsociados.length) parts.push(`Ya asociados: ${yaAsociados.length}`);
  if (noEncontrados.length) parts.push(`No encontrados: ${noEncontrados.length}`);
  if (noRol.length) parts.push(`Sin rol Profesor: ${noRol.length}`);
  if (noColegio.length) parts.push(`Fuera del colegio: ${noColegio.length}`);
  return parts.join(' | ');
};

const AdminCreateCurso = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const stateColegioId = location.state?.idColegio || null;
  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => stateColegioId || ColegioService.getActiveColegioId(), [stateColegioId]);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';
  const [anios, setAnios] = useState([]);
  const [divisiones, setDivisiones] = useState([]);
  const [profesoresDisponibles, setProfesoresDisponibles] = useState([]);
  const [selectedProfesorId, setSelectedProfesorId] = useState('');
  const [profesoresAsignados, setProfesoresAsignados] = useState([]);

  const [form, setForm] = useState({
    idColegio: activeColegioId || '',
    idAnio: '',
    cicloLectivo: new Date().getFullYear(),
    division: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    const fetchAnios = async () => {
      try {
        const list = await materiaService.getAniosActivos();
        setAnios(Array.isArray(list) ? list : []);
      } catch (error) {
        console.warn('No pudimos cargar los años:', error);
      }
    };

    const fetchProfesores = async () => {
      if (!activeColegioId) return;
      try {
        const list = await usuarioService.getProfesoresPorColegio(activeColegioId);
        setProfesoresDisponibles(Array.isArray(list) ? list : []);
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar los profesores.');
        setMessageType('error');
      }
    };

    const fetchDivisiones = async () => {
      if (!activeColegioId) return;
      try {
        const list = await divisionService.getDivisionesActivas(activeColegioId);
        setDivisiones(Array.isArray(list) ? list : []);
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar las divisiones.');
        setMessageType('error');
      }
    };

    fetchAnios();
    fetchDivisiones();
    fetchProfesores();

    if (activeColegioId && !form.idColegio) {
      setForm((prev) => ({ ...prev, idColegio: activeColegioId }));
    }
  }, [activeColegioId, form.idColegio]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddProfesor = () => {
    if (!selectedProfesorId) return;
    const profesor = profesoresDisponibles.find((p) => (p.id || p.Id) === selectedProfesorId);
    if (!profesor) return;
    if (profesoresAsignados.some((p) => (p.id || p.Id) === selectedProfesorId)) return;
    setProfesoresAsignados((prev) => [...prev, profesor]);
    setSelectedProfesorId('');
  };

  const handleRemoveProfesor = (profesorId) => {
    setProfesoresAsignados((prev) => prev.filter((p) => (p.id || p.Id) !== profesorId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.idColegio) {
      setMessage('No se pudo determinar el colegio activo.');
      setMessageType('error');
      return;
    }
    if (!form.idAnio) {
      setMessage('Selecciona el año del curso.');
      setMessageType('error');
      return;
    }
    if (!String(form.division || '').trim()) {
      setMessage('Completa la división del curso.');
      setMessageType('error');
      return;
    }
    if (!Number(form.cicloLectivo)) {
      setMessage('Completa el ciclo lectivo.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Creando curso...');
    setMessageType('info');
    try {
      const createRes = await cursoService.createCurso({
        idColegio: form.idColegio,
        idAnio: form.idAnio,
        cicloLectivo: Number(form.cicloLectivo),
        division: form.division
      });

      const cursoId = createRes?.idCurso || createRes?.IdCurso;
      if (cursoId && profesoresAsignados.length > 0) {
        const ids = profesoresAsignados.map((p) => p.id || p.Id).filter(Boolean);
        const assocRes = await cursoService.assignProfesores(cursoId, ids);
        const summary = summarizeAssociation(assocRes);
        setMessage(summary ? `Curso creado. ${summary}` : 'Curso creado y profesores asociados correctamente.');
      } else {
        setMessage('Curso creado correctamente.');
      }

      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'cursos' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos crear el curso.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <AdminMenu
          activeId="cursos"
          user={user}
          featureFlags={ColegioService.getFeatureFlags()}
          baseItems={ADMIN_DASHBOARD_MENU_ITEMS}
          onNavigate={(item) => navigateAdminMenuItem(navigate, item)}
        />
        <div className="sidebar-footer">
          <ActiveColegioBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Alta de curso</h1>
            <p className="page-subtitle">Crea un curso nuevo para el colegio {activeColegioName}.</p>
          </div>
          <div className="header-right">
            <ActiveColegioBadge />
            <UserSessionMenu user={user} onLogout={logout} showStandaloneLogout={false} userFallbackLabel="Administrador" />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-layer-group" />
                <span>Datos del curso</span>
              </div>
            </div>
            <div className="card-content" style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: '1.5rem' }}>
              <div>
                <p style={{ marginTop: 0, color: '#475569' }}>
                  Define el ciclo lectivo, año y división del curso.
                </p>
                <form onSubmit={handleSubmit} className="prof-create-form">
                  <div className="form-field">
                    <label>Ciclo lectivo</label>
                    <input
                      type="number"
                      value={form.cicloLectivo}
                      onChange={(e) => handleChange('cicloLectivo', e.target.value)}
                      placeholder="2026"
                    />
                  </div>
                  <div className="form-field">
                    <label>Año</label>
                    <select value={form.idAnio} onChange={(e) => handleChange('idAnio', e.target.value)}>
                      <option value="">Selecciona un año</option>
                      {anios.map((anio) => (
                        <option key={anio.id || anio.Id} value={anio.id || anio.Id}>
                          {anio.valor || anio.Valor}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>División</label>
                    <select
                      value={form.division}
                      onChange={(e) => handleChange('division', e.target.value)}
                    >
                      <option value="">Selecciona una división</option>
                      {divisiones.map((division) => (
                        <option key={division.id || division.Id} value={division.nombre || division.Nombre || ''}>
                          {division.nombre || division.Nombre}
                        </option>
                      ))}
                    </select>
                    <small className="helper-text">
                      {divisiones.length > 0
                        ? 'Las divisiones se administran desde el menú Divisiones.'
                        : 'Todavía no hay divisiones activas. Crea una antes de dar de alta el curso.'}
                    </small>
                  </div>

                  {message && (
                    <div
                      className="modal-message"
                      style={{
                        marginTop: '0.5rem',
                        borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                        background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                        color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                      }}
                    >
                      {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                      {message}
                    </div>
                  )}

                  <div className="form-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin-dashboard', { state: { tab: 'cursos' } })}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                      <span>Crear curso</span>
                    </button>
                  </div>
                </form>
              </div>

              <div style={{ borderLeft: '1px solid var(--gray-200)', paddingLeft: '1.25rem' }}>
                <div className="card-title" style={{ marginBottom: '0.75rem' }}>
                  <i className="fas fa-chalkboard-teacher" style={{ color: '#3b82f6' }} />
                  <span>Profesores</span>
                </div>
                <p style={{ marginTop: 0, color: '#475569', fontSize: '0.95rem' }}>
                  Puedes dejar profesores preseleccionados para asociarlos apenas se cree el curso.
                </p>

                {profesoresDisponibles.length > 0 && (
                  <div className="form-field">
                    <label>Seleccionar profesor</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <select value={selectedProfesorId} onChange={(e) => setSelectedProfesorId(e.target.value)} style={{ flex: 1 }}>
                        <option value="">Elige un profesor</option>
                        {profesoresDisponibles.map((prof) => (
                          <option key={prof.id || prof.Id} value={prof.id || prof.Id}>
                            {prof.displayName || `${prof.nombre || ''} ${prof.apellido || ''}`.trim() || prof.email}
                          </option>
                        ))}
                      </select>
                      <button type="button" className="btn btn-primary" onClick={handleAddProfesor}>
                        <i className="fas fa-plus" />
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '1rem' }}>
                  <p style={{ margin: '0 0 0.5rem 0', color: '#475569', fontWeight: 600 }}>Profesores a asociar</p>
                  {profesoresAsignados.length === 0 && (
                    <p style={{ color: '#6b7280', margin: 0 }}>Aún no hay profesores seleccionados.</p>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {profesoresAsignados.map((prof) => {
                      const name = prof.displayName || `${prof.nombre || ''} ${prof.apellido || ''}`.trim() || prof.email;
                      const profId = prof.id || prof.Id;
                      return (
                        <div key={profId} style={{ border: '1px solid var(--gray-200)', borderRadius: '999px', padding: '0.35rem 0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: '#f8fafc' }}>
                          <i className="fas fa-chalkboard-teacher" style={{ color: '#3b82f6' }} />
                          <span>{name}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveProfesor(profId)}
                            style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}
                          >
                            <i className="fas fa-times" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminCreateCurso;
